'use client';

import { JSX, useEffect, useRef, useState } from 'react';

import ArrowInCircle from '@videoblocks/react-icons/ArrowInCircle';
import ArrowInCircleFilled from '@videoblocks/react-icons/ArrowInCircleFilled';

import ProgressiveImage from '../elements/ProgressiveImage/ProgressiveImage';

interface VideoCardProps {
    artistName?: string;
    backgroundColor?: string;
    description: string;
    href: string;
    imagePriority?: boolean;
    portraitSrc?: string;
    portraitAltText?: string;
    title: string;
    videoPosterSrc?: string;
    videoPosterAltText?: string;
    videoSrc?: string;
    lazyLoadImage?: boolean;
}

const VideoCard = ({
    artistName,
    backgroundColor = 'transparent',
    description,
    href,
    imagePriority = false,
    portraitSrc = '',
    portraitAltText = '',
    title,
    videoPosterSrc = '',
    videoPosterAltText = '',
    videoSrc,
    lazyLoadImage = true,
}: VideoCardProps): JSX.Element => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [hovering, setHovering] = useState(false);
    const [iconHovering, setIconHovering] = useState(false);
    const isTransparent = backgroundColor === 'transparent';
    const bgColor = isTransparent ? 'transparent' : `bg-${backgroundColor.toLowerCase()}`;
    const textPadding = !isTransparent ? 'px-6 pb-6' : '';

    useEffect(() => {
        if (videoRef.current) {
            if (hovering) {
                void videoRef.current.play();
            } else {
                videoRef.current.pause();
            }
        }
    }, [hovering]);

    const imageContainerClassNameWithoutVideo = 'relative w-full h-full';
    const imageContainerClassNameWithVideo =
        'absolute w-full z-10 bottom-0 left-0 right-0 top-0 opacity-100 transition-opacity duration-200 ease-in group-hover:opacity-0';

    return (
        <a
            className={`w-full flex flex-col gap-y-6 ${bgColor} group`}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            href={href}
        >
            <div className="w-full h-full relative overflow-hidden">
                {videoSrc && (
                    <video
                        src={videoSrc}
                        data-testid="video-card-video"
                        loop
                        muted
                        preload="metadata"
                        className="relative transform scale-125 min-w-full h-auto z-0"
                        ref={videoRef}
                    />
                )}
                <div
                    data-testid="video-card-image-container"
                    className={videoSrc ? imageContainerClassNameWithVideo : imageContainerClassNameWithoutVideo}
                    style={{ minHeight: `${videoSrc ? '' : '30vh'}` }}
                >
                    <ProgressiveImage
                        src={videoPosterSrc}
                        alt={videoPosterAltText}
                        fill
                        className="object-cover"
                        priority={imagePriority}
                        shouldLazyLoad={lazyLoadImage}
                    />
                    <ProgressiveImage
                        className="absolute z-20 bottom-4 right-4"
                        src={portraitSrc}
                        alt={portraitAltText}
                        width={100}
                        height={100}
                        priority={imagePriority}
                        shouldLazyLoad={lazyLoadImage}
                    />
                </div>
            </div>

            <div className={`flex flex-col gap-y-2 text-white font-polysans ${textPadding}`}>
                <div className="flex gap-x-2 justify-between items-center">
                    <div className="flex flex-col">
                        <h3 className="text-2xl m-0 font-semibold">{title}</h3>
                        <p className="font-polysansMedianItalic text-gray-300 text-lg font-semibold m-0">
                            {artistName}
                        </p>
                    </div>
                    <div onMouseEnter={() => setIconHovering(true)} onMouseLeave={() => setIconHovering(false)}>
                        {iconHovering ? (
                            <ArrowInCircleFilled className="w-12 h-12" />
                        ) : (
                            <ArrowInCircle className="fill-none w-12 h-12" />
                        )}
                    </div>
                </div>
                <p className="font-polysansNeutral text-lg m-0">{description}</p>
            </div>
        </a>
    );
};

export default VideoCard;
